import { getRequiredEntityAlias } from "../entity-aliases.js";
export const PROJECT_LEVEL_ROUTE_PATHS = {
    projectDocuments: "/documents",
    aiAssistant: "/ai-assistant",
    aiAssistantNewChat: "/ai-assistant/new-chat",
    aiAssistantHistory: "/ai-assistant/history",
    specificationClarificationRequests: "/clarifications",
    knowledge: "/knowledge",
    specificationVersionSnapshots: "/version-snapshots",
    specificationQualityGuard: "/quality-guard",
    projectQualityGuardConfiguration: "/quality-guard/configuration",
};
export const AI_ASSISTANT_THREAD_PATH_PREFIX = "/ai-assistant/thread/";
export function buildAssistantThreadPath(threadId) {
    return AI_ASSISTANT_THREAD_PATH_PREFIX + encodeURIComponent(threadId);
}
export function parseAssistantThreadIdFromPath(path) {
    if (!path.startsWith(AI_ASSISTANT_THREAD_PATH_PREFIX)) {
        return null;
    }
    const raw = path.slice(AI_ASSISTANT_THREAD_PATH_PREFIX.length);
    return raw.length > 0 ? decodeURIComponent(raw) : null;
}
export function buildProjectSnapshotPath(releaseNumber) {
    if (releaseNumber === null || releaseNumber === undefined || releaseNumber === "") {
        return PROJECT_LEVEL_ROUTE_PATHS.specificationVersionSnapshots;
    }
    return normalizeProjectPath(`${PROJECT_LEVEL_ROUTE_PATHS.specificationVersionSnapshots}/${releaseNumber}`);
}
export function isProjectSnapshotPath(path) {
    const normalizedPath = normalizeProjectPath(path);
    return (normalizedPath === PROJECT_LEVEL_ROUTE_PATHS.specificationVersionSnapshots ||
        normalizedPath.startsWith(`${PROJECT_LEVEL_ROUTE_PATHS.specificationVersionSnapshots}/`));
}
export function splitPath(path) {
    return normalizeProjectPath(path).split("/").filter(Boolean);
}
function resolveEntityAlias(aliases, entityTypeKey) {
    return getRequiredEntityAlias(aliases, entityTypeKey);
}
export function normalizeProjectPath(path) {
    const trimmed = (path || "").trim();
    if (!trimmed || trimmed === "/")
        return "/";
    const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    const normalized = withLeadingSlash.replace(/\/+/g, "/");
    if (normalized.length > 1 && normalized.endsWith("/")) {
        return normalized.slice(0, -1);
    }
    return normalized;
}
export function buildProjectAppPath(args) {
    const normalizedProjectPath = normalizeProjectPath(args.projectPath ?? "/");
    return normalizedProjectPath === "/"
        ? `/project/${encodeURIComponent(args.projectSlug)}`
        : `/project/${encodeURIComponent(args.projectSlug)}${normalizedProjectPath}`;
}
export function parseProjectAppPath(path) {
    const segments = splitPath(path);
    if (segments[0] !== "project" || !segments[1]) {
        return null;
    }
    return {
        projectSlug: decodeURIComponent(segments[1]),
        projectPath: segments.length > 2 ? normalizeProjectPath(`/${segments.slice(2).join("/")}`) : "/",
    };
}
export function buildFolderPath(intent) {
    const folderSegments = intent.scope.folderSlugs.filter(Boolean).map(encodeURIComponent);
    return normalizeProjectPath(`/${encodeURIComponent(intent.scope.rootFolderSlug)}/${folderSegments.join("/")}`);
}
export function buildEntityPath(intent) {
    const folderPrefix = buildFolderPath({ kind: "folder", scope: intent.scope });
    const lineageSegments = intent.scope.entityLineage.flatMap((lineage) => [
        encodeURIComponent(lineage.entityAlias),
        encodeURIComponent(lineage.entitySlug),
    ]);
    return normalizeProjectPath(`${folderPrefix}/${lineageSegments.join("/")}/${encodeURIComponent(intent.entityAlias)}/${encodeURIComponent(intent.entitySlug)}`);
}
export function resolveEntityRouteFromTreeSelection(input) {
    const entityTypeKey = input.entityTypeKey;
    const entityAlias = resolveEntityAlias(input.aliases, entityTypeKey);
    return buildEntityPath({
        kind: "entity",
        entityTypeKey,
        entityAlias,
        entitySlug: input.entitySlug,
        scope: {
            rootFolderSlug: input.rootFolderSlug,
            folderSlugs: input.folderSlugs ?? [],
            entityLineage: (input.entityLineage ?? []).map((lineage) => ({
                entityTypeKey: lineage.entityTypeKey,
                entityAlias: lineage.entityAlias ?? resolveEntityAlias(input.aliases, lineage.entityTypeKey),
                entitySlug: lineage.entitySlug,
            })),
        },
    });
}
export function stripCollectionSlugFromFolderScope(scope, collectionSlug) {
    const lastFolderSlug = scope.folderSlugs.length > 0 ? scope.folderSlugs[scope.folderSlugs.length - 1] : undefined;
    if (!collectionSlug || lastFolderSlug !== collectionSlug) {
        return scope;
    }
    return {
        ...scope,
        folderSlugs: scope.folderSlugs.slice(0, -1),
    };
}
function folderNodeRootSlug(node) {
    return node.rootFolderSlug || node.rootFolderKey;
}
export function buildDefaultRootFolderSlugByEntityType(structure) {
    if (!structure)
        return {};
    const result = {};
    for (const entityDisplay of structure.entityDisplays ?? []) {
        if (entityDisplay.entityTypeKey && entityDisplay.canonicalRootFolderSlug) {
            result[entityDisplay.entityTypeKey] = entityDisplay.canonicalRootFolderSlug;
        }
    }
    const rootOrderByKey = new Map();
    for (const rootFolder of structure.rootFolders ?? []) {
        rootOrderByKey.set(rootFolder.key, rootFolder.listOrder);
    }
    const candidates = (structure.folderNodes ?? [])
        .filter((node) => !!node &&
        node.kind === "collection" &&
        typeof node.entityTypeKey === "string" &&
        node.entityTypeKey.length > 0)
        .sort((a, b) => {
        const rootA = rootOrderByKey.get(a.rootFolderKey) ?? Number.MAX_SAFE_INTEGER;
        const rootB = rootOrderByKey.get(b.rootFolderKey) ?? Number.MAX_SAFE_INTEGER;
        if (rootA !== rootB)
            return rootA - rootB;
        return a.listOrder - b.listOrder;
    });
    for (const node of candidates) {
        if (!node.entityTypeKey)
            continue;
        if (!result[node.entityTypeKey]) {
            result[node.entityTypeKey] = folderNodeRootSlug(node);
        }
    }
    return result;
}
export function resolveDefaultEntityPath(input) {
    const canonicalEntityType = input.entityTypeKey;
    const rootFolderSlug = input.rootFolderSlugByEntityType[canonicalEntityType];
    if (!rootFolderSlug)
        return null;
    return buildEntityPath({
        kind: "entity",
        entityTypeKey: canonicalEntityType,
        entityAlias: resolveEntityAlias(input.aliases, canonicalEntityType),
        entitySlug: input.entitySlug,
        scope: { rootFolderSlug, folderSlugs: [], entityLineage: [] },
    });
}
export function resolveDefaultEntityPathFromStructure(input) {
    const rootFolderSlugByEntityType = buildDefaultRootFolderSlugByEntityType(input.structure);
    return resolveDefaultEntityPath({
        entityTypeKey: input.entityTypeKey,
        entitySlug: input.entitySlug,
        aliases: input.aliases,
        rootFolderSlugByEntityType,
    });
}
